/**
 * ValidatedInput - Input with live validation feedback
 * Issue #1101: Centralized live validation system
 *
 * Wraps a standard input with visual validation feedback.
 * Uses centralized validation rules - just specify the rule type.
 *
 * @example
 * ```tsx
 * <ValidatedInput
 *   validate="email"
 *   value={email}
 *   onChange={setEmail}
 *   placeholder="Enter your email"
 * />
 * ```
 */

import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { Check, AlertCircle } from 'lucide-react';
import { OroLoader } from './OroLoader';
import { useValidation, type UseValidationOptions } from '../hooks/useValidation';
import type { ValidationRuleType } from '../lib/validation/rules';

interface ValidatedInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Validation rule type from the registry */
  validate: ValidationRuleType;
  /** Current input value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Optional label */
  label?: string;
  /** Show label as required */
  required?: boolean;
  /** Additional validation options */
  validationOptions?: UseValidationOptions;
  /** Show validation status icon */
  showIcon?: boolean;
  /** Show validation message below input */
  showMessage?: boolean;
  /** Custom error message override */
  errorMessage?: string;
  /** Async validation in progress */
  isValidating?: boolean;
}

const ValidatedInput = forwardRef<HTMLInputElement, ValidatedInputProps>(
  (
    {
      validate,
      value,
      onChange,
      label,
      required = false,
      validationOptions,
      showIcon = true,
      showMessage = true,
      errorMessage,
      isValidating = false,
      className = '',
      id,
      ...inputProps
    },
    ref
  ) => {
    const { status, message, touched, setTouched } = useValidation(
      value,
      validate,
      validationOptions
    );

    // Determine visual state
    const showValidation = touched && value.length > 0;
    const isInvalid = showValidation && status === 'invalid';
    const isValid = showValidation && status === 'valid';

    // Build input classes
    const inputClasses = [
      'w-full bg-oro-surface-alt border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted',
      'focus:outline-none focus:ring-2 focus:border-transparent transition-colors',
      // Right padding for icon
      showIcon ? 'pr-10' : '',
      // Border colors based on state
      isInvalid
        ? 'border-oro-error focus:ring-oro-error/30'
        : isValid
          ? 'border-oro-success focus:ring-oro-success/30'
          : 'border-oro-border focus:ring-accent',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Generate ID if not provided (stable across renders)
    const generatedId = useId();
    const inputId = id || `input-${validate}-${generatedId}`;

    return (
      <div className="space-y-1">
        {/* Label */}
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-text-secondary">
            {label}
            {required && <span className="text-oro-error ml-1">*</span>}
          </label>
        )}

        {/* Input wrapper */}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => setTouched(true)}
            className={inputClasses}
            aria-invalid={isInvalid}
            aria-describedby={isInvalid ? `${inputId}-error` : undefined}
            {...inputProps}
          />

          {/* Status icon */}
          {showIcon && showValidation && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isValidating ? (
                <OroLoader variant="bounce" size={16} label="Validating" />
              ) : isValid ? (
                <Check className="w-4 h-4 text-oro-success" />
              ) : isInvalid ? (
                <AlertCircle className="w-4 h-4 text-oro-error" />
              ) : null}
            </div>
          )}
        </div>

        {/* Error message */}
        {showMessage && isInvalid && (
          <p id={`${inputId}-error`} className="text-sm text-oro-error flex items-center gap-1">
            {errorMessage || message}
          </p>
        )}
      </div>
    );
  }
);

ValidatedInput.displayName = 'ValidatedInput';

export default ValidatedInput;

/**
 * ValidatedTextarea - Textarea with live validation feedback
 */
interface ValidatedTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  validate: ValidationRuleType;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  validationOptions?: UseValidationOptions;
  showMessage?: boolean;
  showCharCount?: boolean;
  maxLength?: number;
}

export function ValidatedTextarea({
  validate,
  value,
  onChange,
  label,
  required = false,
  validationOptions,
  showMessage = true,
  showCharCount = false,
  maxLength,
  className = '',
  id,
  ...textareaProps
}: ValidatedTextareaProps) {
  const { status, message, touched, setTouched } = useValidation(
    value,
    validate,
    validationOptions
  );

  const showValidation = touched && value.length > 0;
  const isInvalid = showValidation && status === 'invalid';
  const isValid = showValidation && status === 'valid';

  const textareaClasses = [
    'w-full bg-oro-surface-alt border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted',
    'focus:outline-none focus:ring-2 focus:border-transparent transition-colors resize-none',
    isInvalid
      ? 'border-oro-error focus:ring-oro-error/30'
      : isValid
        ? 'border-oro-success focus:ring-oro-success/30'
        : 'border-oro-border focus:ring-accent',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const generatedTextareaId = useId();
  const textareaId = id || `textarea-${validate}-${generatedTextareaId}`;

  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={textareaId} className="block text-sm font-medium text-text-secondary">
          {label}
          {required && <span className="text-oro-error ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <textarea
          id={textareaId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          className={textareaClasses}
          maxLength={maxLength}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? `${textareaId}-error` : undefined}
          {...textareaProps}
        />

        {/* Character count */}
        {showCharCount && maxLength && (
          <span className="absolute bottom-2 right-3 text-xs text-text-muted">
            {value.length}/{maxLength}
          </span>
        )}
      </div>

      {showMessage && isInvalid && (
        <p id={`${textareaId}-error`} className="text-sm text-oro-error">
          {message}
        </p>
      )}
    </div>
  );
}
